import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { emergencyServicesAPI, emergencyLinksAPI } from '../../api/client';
import { INSTITUTION_TYPES, DEPENDENCY_TYPES, CARE_LEVELS } from '../../lib/emergencyConstants';
import { useAuth } from '../../context/AuthContext';
import { MAPS_KEY } from '../../lib/locationConstants';
import { ArrowLeft, Edit2, Phone, Clock, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

export default function EmergencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    emergencyServicesAPI.get(id).then(r => setService(r.data)).catch(() => toast.error('Error al cargar')).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!service) return <div className="text-center py-12 text-slate-400">Servicio no encontrado.</div>;

  const it = INSTITUTION_TYPES[service.institution_type] || INSTITUTION_TYPES.hospital;
  const dep = DEPENDENCY_TYPES[service.dependency_type] || DEPENDENCY_TYPES.publica;
  const addr = service.address || {};
  const gps = service.gps_location;
  const addrStr = [addr.street, addr.ext_number, addr.neighborhood, addr.city, addr.state].filter(Boolean).join(', ');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emergency-detail">
      <div className="flex items-start gap-3">
        <button aria-label="Volver a servicios de emergencia" onClick={() => navigate('/dashboard/emergency-services')} className="soem-btn-secondary p-2 mt-1"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-3xl">{it.emoji}</span>
            <h1 className="text-2xl font-bold text-soem-navy">{service.name}</h1>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${dep.bg}`}>{dep.label}</span>
            {service.care_level && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">Nivel {service.care_level}</span>}
            <span className="text-slate-400 text-xs font-mono">{service.service_code}</span>
          </div>
        </div>
        {canEdit && (
          <button onClick={() => navigate(`/dashboard/emergency-services/${id}/edit`)}
            className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="edit-btn">
            <Edit2 size={14} /> Editar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {service.phone && (
            <div><p className="soem-label mb-1 flex items-center gap-1"><Phone size={11} /> Teléfono</p>
              <p className="text-sm font-medium text-soem-navy">{service.phone}</p></div>
          )}
          {service.contact_name && (
            <div><p className="soem-label mb-1">Contacto</p>
              <p className="text-sm font-medium text-soem-navy">{service.contact_name}</p></div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {service.estimated_response_time && (
              <div><p className="soem-label mb-1 flex items-center gap-1"><Clock size={11} /> Tiempo de respuesta</p>
                <p className="text-sm font-medium text-soem-navy">{service.estimated_response_time} min</p></div>
            )}
            {service.distance_km && (
              <div><p className="soem-label mb-1 flex items-center gap-1"><MapPin size={11} /> Distancia</p>
                <p className="text-sm font-medium text-soem-navy">{service.distance_km} km</p></div>
            )}
          </div>
          {service.institution_type === 'hospital' && service.care_level && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
              <p className="font-semibold text-blue-800 text-sm">Nivel de Atención {service.care_level}</p>
              <p className="text-blue-600 text-xs mt-0.5">{CARE_LEVELS[service.care_level]?.desc}</p>
            </div>
          )}
          {service.notes && (
            <div><p className="soem-label mb-1">Notas</p>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm border">{service.notes}</p></div>
          )}
        </div>

        <div className="space-y-4">
          {addrStr && (
            <div><p className="soem-label mb-1 flex items-center gap-1"><MapPin size={11} /> Dirección</p>
              <p className="text-sm text-soem-navy">{addrStr}</p></div>
          )}
          {gps && (
            <div>
              <p className="soem-label mb-2">Ubicación</p>
              <div className="rounded-sm overflow-hidden border border-slate-200">
                <iframe title="map" src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${gps.lat},${gps.lng}&zoom=15`}
                  width="100%" height="200" style={{ border: 0 }} allowFullScreen loading="lazy" />
              </div>
              <p className="text-xs text-slate-400 font-mono mt-1">{gps.lat}, {gps.lng}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
